import { Box, BoxProps } from "@chakra-ui/react"
import { Position } from "@pathwright/utils/src/positioning/fractional-positioning"
import { MotionProps, motion } from "framer-motion"
import { forwardRef, useEffect, useState } from "react"
import { usePreviousDistinct } from "react-use"

type LayoutItemProps = Omit<BoxProps, "children"> & MotionProps

// AnimatePresence measures this node directly when a card is dismissed.
const LayoutItem = forwardRef<HTMLLIElement, LayoutItemProps>(
  function LayoutItem({ children }, ref) {
    return (
      <Box asChild w="full" _empty={{ display: "none" }}>
        <motion.li
          ref={ref}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          layout
        >
          {children}
        </motion.li>
      </Box>
    )
  }
)

type PositionedLayoutItemProps = {
  position: Position
} & Omit<LayoutItemProps, "position">

export function PositionedLayoutItem({
  position,
  ...layoutItemProps
}: PositionedLayoutItemProps) {
  // If the item is repositioning, then we need to bumpt the zIndex
  // to ensure it overlays all other items in the list when moving
  // (so as not to move while appearing behind the other items).
  const [isRepositioning, setIsRepositioning] = useState(false)

  // Identify if this item is being moved by comparing change in position.
  const prevPosition = usePreviousDistinct(position)
  useEffect(() => {
    const positionChanged =
      typeof prevPosition !== "undefined" && prevPosition !== position
    if (positionChanged) setIsRepositioning(true)
  }, [prevPosition])

  return (
    <LayoutItem
      onLayoutAnimationComplete={() => setIsRepositioning(false)}
      position="relative"
      zIndex={isRepositioning ? 10 : "auto"}
      {...layoutItemProps}
    />
  )
}

export default LayoutItem
